import React from 'react';
import { createRoot } from 'react-dom/client';
import { configureApi } from '@round/api';
import 'react-loading-skeleton/dist/skeleton.css';
import 'react-datepicker/dist/react-datepicker.min.css';
import '@round/ui-kit/dist/esm/index.css';
import '@round/auth/dist/esm/index.css';
import '@round/creatorbase-ui/dist/esm/index.css';
import '@round/creatorbase-helpers/dist/esm/index.css';
import '@round/whitelabel-ui/dist/esm/index.css';
import './chartjs-setup';
import 'flag-icons/css/flag-icons.min.css';

import './index.css';
import App from './App';
import packageJson from '../package.json';

import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { installPreloadErrorHandler } from '@round/utils';
import { FeatureFlagsProvider } from 'utility/featureFlags/FeatureFlagsContext';
import { MockProvider } from './utility/mock/MockContext';
import { Tooltip } from '@round/creatorbase-ui';
import { handleLogErrors } from '@/utility/ErrorBoundary/ErrorBoundary';
import { hydrateSelectedClientFromUrl } from '@round/creatorbase-helpers';

configureApi({ origin: import.meta.env.VITE_API_DOMAIN ?? '' });

// Before mount: apply a client selection handed over from another app via the URL.
hydrateSelectedClientFromUrl();

const datadog_token = import.meta.env.VITE_DATADOG_TOKEN || '';
if (import.meta.env.PROD && datadog_token) {
    datadogLogs.init({
        clientToken: datadog_token,
        site: import.meta.env.VITE_DATADOG_SITE_NAME || '',
        forwardErrorsToLogs: true,
        sampleRate: 100,
    });
}

if (
    import.meta.env.PROD &&
    import.meta.env.VITE_DD_ROUND_FRONTEND_APPLICATION_ID &&
    import.meta.env.VITE_DD_ROUND_FRONTEND_CLIENT_TOKEN
) {
    datadogRum.init({
        applicationId: import.meta.env.VITE_DD_ROUND_FRONTEND_APPLICATION_ID,
        clientToken: import.meta.env.VITE_DD_ROUND_FRONTEND_CLIENT_TOKEN,
        site: import.meta.env.VITE_DD_ROUND_FRONTEND_SITE || '',
        service: import.meta.env.VITE_DD_ROUND_FRONTEND_SERVICE || '',
        env: import.meta.env.VITE_DD_ROUND_FRONTEND_ENV || '',
        version: packageJson.version,
        sessionSampleRate: 100,
        sessionReplaySampleRate: 100,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: 'mask-user-input',
    });
}

installPreloadErrorHandler({ onError: (error) => handleLogErrors(error) });

const root = createRoot(document.getElementById('root')!);

root.render(
    <FeatureFlagsProvider>
        <MockProvider>
            <Tooltip.Provider>
                <App />
            </Tooltip.Provider>
        </MockProvider>
    </FeatureFlagsProvider>
);
